/**
 * Attachment Cache Service
 * Manages local caching of aviso attachments (PDFs, images) using SQLite + Filesystem
 * This prevents files from being downloaded to the public Downloads folder
 * and enables automatic cleanup when avisos expire
 */
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { CapacitorSQLite, SQLiteConnection, SQLiteDBConnection } from '@capacitor-community/sqlite';

const DB_NAME = 'tuvisa_cache';
const TABLE_NAME = 'cached_attachments';

class AttachmentCacheService {
    static isNative = Capacitor.isNativePlatform();
    static sqliteConnection = null;
    static db = null;
    static isInitialized = false;

    /**
     * Initialize the SQLite database and create tables if needed
     */
    static async initDatabase() {
        if (!this.isNative) {
            console.log('AttachmentCacheService: Not available on web platform');
            return false;
        }

        if (this.isInitialized) {
            return true;
        }

        try {
            this.sqliteConnection = new SQLiteConnection(CapacitorSQLite);

            // Check connection consistency (required for Android)
            const retCC = await this.sqliteConnection.checkConnectionsConsistency();
            const isConn = (await this.sqliteConnection.isConnection(DB_NAME, false)).result;

            if (retCC.result && isConn) {
                this.db = await this.sqliteConnection.retrieveConnection(DB_NAME, false);
            } else {
                this.db = await this.sqliteConnection.createConnection(
                    DB_NAME,
                    false,
                    'no-encryption',
                    1,
                    false
                );
            }

            await this.db.open();

            // Create table if not exists
            const createTableSQL = `
                CREATE TABLE IF NOT EXISTS ${TABLE_NAME} (
                    id INTEGER PRIMARY KEY AUTOINCREMENT,
                    adjunto_id INTEGER UNIQUE NOT NULL,
                    aviso_id INTEGER NOT NULL,
                    local_path TEXT NOT NULL,
                    filename TEXT NOT NULL,
                    mime_type TEXT,
                    file_size INTEGER DEFAULT 0,
                    cached_at TEXT NOT NULL
                );
                CREATE INDEX IF NOT EXISTS idx_aviso_id ON ${TABLE_NAME}(aviso_id);
            `;

            await this.db.execute(createTableSQL);

            this.isInitialized = true;
            console.log('AttachmentCacheService: Database initialized successfully');
            return true;
        } catch (error) {
            console.error('AttachmentCacheService: Failed to initialize database:', error);
            return false;
        }
    }

    /**
     * Check if an attachment is already cached
     * @param {number} adjuntoId - The attachment ID from the server
     * @returns {Promise<string|null>} - Local file path or null if not cached
     */
    static async getCachedPath(adjuntoId) {
        if (!this.isNative || !this.isInitialized) return null;

        try {
            const result = await this.db.query(
                `SELECT local_path FROM ${TABLE_NAME} WHERE adjunto_id = ?`,
                [adjuntoId]
            );

            if (result.values && result.values.length > 0) {
                const localPath = result.values[0].local_path;

                // Verify file still exists
                try {
                    await Filesystem.stat({
                        path: localPath,
                        directory: Directory.Documents
                    });
                    return localPath;
                } catch (e) {
                    // File doesn't exist, remove stale record
                    await this.db.run(
                        `DELETE FROM ${TABLE_NAME} WHERE adjunto_id = ?`,
                        [adjuntoId]
                    );
                    return null;
                }
            }
            return null;
        } catch (error) {
            console.error('AttachmentCacheService: Error checking cache:', error);
            return null;
        }
    }

    /**
     * Cache an attachment file
     * @param {number} adjuntoId - The attachment ID from the server
     * @param {number} avisoId - The aviso ID this attachment belongs to
     * @param {Blob} blob - The file content as a Blob
     * @param {string} filename - Original filename
     * @param {string} mimeType - MIME type of the file
     * @returns {Promise<string|null>} - Local file path or null on failure
     */
    static async cacheAttachment(adjuntoId, avisoId, blob, filename, mimeType) {
        if (!this.isNative) return null;

        // Auto-initialize if needed
        if (!this.isInitialized) {
            console.log('AttachmentCacheService: Auto-initializing...');
            const initialized = await this.initDatabase();
            if (!initialized) {
                console.error('AttachmentCacheService: Failed to auto-initialize');
                return null;
            }
        }

        try {
            // Generate unique filename to avoid conflicts
            const ext = filename.split('.').pop() || 'bin';
            const localFilename = `attachments/${avisoId}_${adjuntoId}_${Date.now()}.${ext}`;

            // Convert blob to base64
            const base64Data = await this._blobToBase64(blob);

            // Write file to app's private data directory
            await Filesystem.writeFile({
                path: localFilename,
                data: base64Data,
                directory: Directory.Documents,
                recursive: true
            });

            // Save record to SQLite
            await this.db.run(
                `INSERT OR REPLACE INTO ${TABLE_NAME} 
                 (adjunto_id, aviso_id, local_path, filename, mime_type, file_size, cached_at) 
                 VALUES (?, ?, ?, ?, ?, ?, ?)`,
                [
                    adjuntoId,
                    avisoId,
                    localFilename,
                    filename,
                    mimeType,
                    blob.size,
                    new Date().toISOString()
                ]
            );

            console.log(`AttachmentCacheService: Cached ${filename} as ${localFilename}`);
            return localFilename;
        } catch (error) {
            console.error('AttachmentCacheService: Error caching attachment:', error);
            return null;
        }
    }


    /**
     * Get the full URI for a cached file (usable with Browser.open or file viewers)
     * @param {string} localPath - The local path stored in the database
     * @returns {Promise<string|null>} - Full file URI or null
     */
    static async getFileUri(localPath) {
        if (!this.isNative) return null;

        try {
            const result = await Filesystem.getUri({
                path: localPath,
                directory: Directory.Documents
            });
            return result.uri;
        } catch (error) {
            console.error('AttachmentCacheService: Error getting file URI:', error);
            return null;
        }
    }

    /**
     * Read a cached file's content as base64
     * @param {string} localPath - The local path stored in the database
     * @returns {Promise<{data: string, mimeType: string}|null>} - Base64 data and mime type, or null
     */
    static async readCachedFile(localPath) {
        if (!this.isNative || !this.isInitialized) return null;

        try {
            // Get mime type from database
            const metaResult = await this.db.query(
                `SELECT mime_type FROM ${TABLE_NAME} WHERE local_path = ?`,
                [localPath]
            );
            const mimeType = metaResult.values?.[0]?.mime_type || 'application/octet-stream';

            // Read file content
            const fileResult = await Filesystem.readFile({
                path: localPath,
                directory: Directory.Documents
            });

            return {
                data: fileResult.data,
                mimeType: mimeType
            };
        } catch (error) {
            console.error('AttachmentCacheService: Error reading cached file:', error);
            return null;
        }
    }

    /**
     * Read a cached file and return as a Blob
     * @param {string} localPath - The local path stored in the database  
     * @returns {Promise<Blob|null>} - Blob object or null
     */
    static async getCachedBlob(localPath) {
        const fileData = await this.readCachedFile(localPath);
        if (!fileData) return null;

        try {
            // Convert base64 to blob
            const byteCharacters = atob(fileData.data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            return new Blob([byteArray], { type: fileData.mimeType });
        } catch (error) {
            console.error('AttachmentCacheService: Error creating blob:', error);
            return null;
        }
    }

    /**
     * Remove cached files for avisos that no longer exist
     * @param {number[]} activeAvisoIds - Array of aviso IDs that are still active
     * @returns {Promise<{deleted: number, freedBytes: number}>}
     */
    static async cleanupOrphans(activeAvisoIds) {
        if (!this.isNative || !this.isInitialized) {
            return { deleted: 0, freedBytes: 0 };
        }

        try {
            // Get all cached avisos that are NOT in the active list
            let orphanQuery;
            let orphanParams = [];

            if (activeAvisoIds.length === 0) {
                // If no active avisos, all cached are orphans
                orphanQuery = `SELECT * FROM ${TABLE_NAME}`;
            } else {
                const placeholders = activeAvisoIds.map(() => '?').join(',');
                orphanQuery = `SELECT * FROM ${TABLE_NAME} WHERE aviso_id NOT IN (${placeholders})`;
                orphanParams = activeAvisoIds;
            }

            const result = await this.db.query(orphanQuery, orphanParams);
            const orphans = result.values || [];

            let deleted = 0;
            let freedBytes = 0;

            for (const orphan of orphans) {
                try {
                    // Delete the file
                    await Filesystem.deleteFile({
                        path: orphan.local_path,
                        directory: Directory.Documents
                    });
                    freedBytes += orphan.file_size || 0;
                    deleted++;
                } catch (e) {
                    // File might already be deleted, that's ok
                    console.warn('Could not delete cached file:', orphan.local_path);
                }

                // Delete the database record
                await this.db.run(
                    `DELETE FROM ${TABLE_NAME} WHERE id = ?`,
                    [orphan.id]
                );
            }

            if (deleted > 0) {
                console.log(`AttachmentCacheService: Cleaned up ${deleted} orphan files, freed ${this._formatBytes(freedBytes)}`);
            }

            return { deleted, freedBytes };
        } catch (error) {
            console.error('AttachmentCacheService: Error during cleanup:', error);
            return { deleted: 0, freedBytes: 0 };
        }
    }

    /**
     * Clear all cached attachments
     * @returns {Promise<{deleted: number, freedBytes: number}>}
     */
    static async clearAllCache() {
        if (!this.isNative || !this.isInitialized) {
            return { deleted: 0, freedBytes: 0 };
        }

        try {
            const result = await this.db.query(`SELECT * FROM ${TABLE_NAME}`);
            const cached = result.values || [];

            let deleted = 0;
            let freedBytes = 0;

            for (const item of cached) {
                try {
                    await Filesystem.deleteFile({
                        path: item.local_path,
                        directory: Directory.Documents
                    });
                    freedBytes += item.file_size || 0;
                    deleted++;
                } catch (e) {
                    // Continue even if file deletion fails
                }
            }

            // Clear the table
            await this.db.run(`DELETE FROM ${TABLE_NAME}`);

            console.log(`AttachmentCacheService: Cleared all cache, ${deleted} files, ${this._formatBytes(freedBytes)}`);
            return { deleted, freedBytes };
        } catch (error) {
            console.error('AttachmentCacheService: Error clearing cache:', error);
            return { deleted: 0, freedBytes: 0 };
        }
    }

    /**
     * Get cache statistics
     * @returns {Promise<{count: number, totalBytes: number}>}
     */
    static async getCacheStats() {
        if (!this.isNative || !this.isInitialized) {
            return { count: 0, totalBytes: 0 };
        }

        try {
            const result = await this.db.query(
                `SELECT COUNT(*) as count, COALESCE(SUM(file_size), 0) as total FROM ${TABLE_NAME}`
            );

            if (result.values && result.values.length > 0) {
                return {
                    count: result.values[0].count || 0,
                    totalBytes: result.values[0].total || 0
                };
            }
            return { count: 0, totalBytes: 0 };
        } catch (error) {
            console.error('AttachmentCacheService: Error getting stats:', error);
            return { count: 0, totalBytes: 0 };
        }
    }

    /**
     * Convert Blob to base64 string
     */
    static _blobToBase64(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                // Remove data URL prefix to get pure base64
                const base64 = reader.result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    /**
     * Format bytes to human readable string
     */
    static _formatBytes(bytes) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
}

export default AttachmentCacheService;
